import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { exchanges } from '@/api/endpoints';
import { Notice } from '@/components/Notice';
import { RequirePermission } from '@/components/RequirePermission';
import { describe } from '@/lib/errors';
import { formatMoney, relativeAge } from '@/lib/format';
import type { ExchangeAccount } from '@/api/types';
import { ConnectionsPanel } from './ConnectionsPanel';

/**
 * One tab per exchange.
 *
 * Capital is not fungible across venues (ARCHITECTURE.md R-5): USDT on Binance
 * cannot fund a Bybit order without a manual transfer nobody has authorised.
 * Presenting the five together in one table invites exactly that mistake, so
 * each exchange gets its own tab with its own float, its own accounts and its
 * own trading state, and a combined figure is shown only where it is honest
 * to do so.
 */
export function ExchangesPage() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState<string | null>(null);

  const registry = useQuery({ queryKey: ['exchanges'], queryFn: exchanges.list });
  const accounts = useQuery({ queryKey: ['exchange-accounts'], queryFn: exchanges.accounts });

  const toggle = useMutation({
    mutationFn: ({ account, enable }: { account: ExchangeAccount; enable: boolean }) =>
      enable ? exchanges.enable(account.id) : exchanges.disable(account.id),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['exchange-accounts'] });
    },
    onError: (err) => setError(describe(err)),
  });

  // Separate from `toggle` because they mean different things: enabling
  // configures an account, starting lets it trade.
  const runState = useMutation({
    mutationFn: ({ account, start }: { account: ExchangeAccount; start: boolean }) =>
      start ? exchanges.start(account.id) : exchanges.pause(account.id),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['exchange-accounts'] });
    },
    onError: (err) => setError(describe(err)),
  });

  const byExchange = useMemo(() => {
    const groups = new Map<string, ExchangeAccount[]>();
    for (const exchange of registry.data ?? []) groups.set(exchange.code, []);
    for (const account of accounts.data ?? []) {
      groups.set(account.exchange_code, [...(groups.get(account.exchange_code) ?? []), account]);
    }
    return groups;
  }, [registry.data, accounts.data]);

  const codes = [...byExchange.keys()];
  const current = active && byExchange.has(active) ? active : (codes[0] ?? null);
  const currentAccounts = current ? (byExchange.get(current) ?? []) : [];
  const currentExchange = (registry.data ?? []).find((exchange) => exchange.code === current);

  const liveEnabled = currentAccounts.filter((a) => a.mode === 'LIVE' && a.is_enabled).length;

  return (
    <>
      <div className="page-head">
        <h1>Exchanges</h1>
        <p>
          Each exchange holds its own USDT and trades independently. Nothing moves between them, so
          a balance here funds orders here and nowhere else.
        </p>
      </div>

      {error && <Notice tone="error">{error}</Notice>}

      <div className="tabs" role="tablist" aria-label="Exchanges">
        {codes.map((code) => {
          const group = byExchange.get(code) ?? [];
          const hasLive = group.some((a) => a.mode === 'LIVE' && a.is_enabled);
          return (
            <button
              key={code}
              type="button"
              role="tab"
              aria-selected={code === current}
              className="tab"
              data-mode={hasLive ? 'LIVE' : group.some((a) => a.is_enabled) ? 'PAPER' : undefined}
              onClick={() => setActive(code)}
            >
              {(registry.data ?? []).find((exchange) => exchange.code === code)?.display_name ?? code}
              <span className="tab-count num">{group.length}</span>
            </button>
          );
        })}
      </div>

      {current === null ? (
        <section className="panel">
          <div className="empty">No exchanges registered.</div>
        </section>
      ) : (
        <>
          {liveEnabled > 0 && (
            <Notice tone="warn">
              {liveEnabled === 1 ? 'One live account is' : `${liveEnabled} live accounts are`} enabled
              on {currentExchange?.display_name ?? current}. Orders here place real money.
            </Notice>
          )}

          <section className="panel">
            <div className="panel-head">
              <h2>{currentExchange?.display_name ?? current}</h2>
              <span className="hint">
                {currentExchange?.requires_passphrase ? 'API passphrase required' : 'API key and secret only'}
              </span>
            </div>

            <div className="panel-body" style={{ display: 'flex', gap: 'var(--space-6)', flexWrap: 'wrap' }}>
              <div className="stat">
                <span className="stat-value num">{currentAccounts.length}</span>
                <span className="stat-label">Accounts on this exchange</span>
              </div>
              <div className="stat">
                <span className="stat-value num">
                  {currentAccounts.filter((a) => a.is_enabled).length}
                </span>
                <span className="stat-label">Enabled</span>
              </div>
              <div className="stat">
                <span className="stat-value num">
                  {currentAccounts.filter((a) => a.trading_state === 'RUNNING').length}
                </span>
                <span className="stat-label">Trading</span>
              </div>
              <div className="stat">
                {/* Balances arrive with the connectors in phase 5; the slot
                    exists now so the per-exchange float has an obvious home. */}
                <span className="stat-value num">{formatMoney(null)}</span>
                <span className="stat-label">Available USDT on this exchange</span>
              </div>
            </div>

            <div className="panel-body flush">
              {currentAccounts.length === 0 ? (
                <div className="empty">
                  No accounts on {currentExchange?.display_name ?? current} yet. Create a paper
                  account to start in simulation.
                </div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Label</th>
                      <th>Mode</th>
                      <th>Enabled</th>
                      <th>State</th>
                      <th>API key</th>
                      <th>Verified</th>
                      <th className="right">Last sync</th>
                      <th className="right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentAccounts.map((account) => (
                      <tr key={account.id}>
                        <td>{account.label}</td>
                        <td>
                          <span className="badge" data-mode={account.mode === 'LIVE' ? 'LIVE' : 'PAPER'}>
                            {account.mode.toLowerCase()}
                          </span>
                          {account.practice_environment === 'DEMO' ? (
                            <span className="muted"> demo trading</span>
                          ) : null}
                        </td>
                        <td>{account.is_enabled ? 'yes' : 'no'}</td>
                        <td>{account.trading_state.toLowerCase().replace('_', ' ')}</td>
                        <td className="num">
                          {account.credentials?.api_key_masked ??
                            (account.mode === 'PAPER' ? 'not needed' : 'not set')}
                        </td>
                        <td>
                          {account.mode === 'PAPER'
                            ? '—'
                            : account.credentials?.verified_at
                              ? 'yes'
                              : 'not yet'}
                        </td>
                        <td className="right">{relativeAge(account.last_sync_at)}</td>
                        <td className="right">
                          <div className="row-actions">
                            <RequirePermission permission="control:trading">
                              {(() => {
                                const control = runControl(account);
                                return (
                                  <button
                                    type="button"
                                    disabled={runState.isPending || control.blocked !== null}
                                    title={control.blocked ?? control.hint}
                                    onClick={() => {
                                      if (
                                        control.label === 'Start' &&
                                        account.mode === 'LIVE' &&
                                        !window.confirm(
                                          `Resume live trading on ${account.label}? New orders will use real funds.`,
                                        )
                                      ) {
                                        return;
                                      }
                                      runState.mutate({ account, start: control.label === 'Start' });
                                    }}
                                  >
                                    {control.label}
                                  </button>
                                );
                              })()}
                            </RequirePermission>
                            <RequirePermission permission="config:exchange_accounts">
                              <button
                                type="button"
                                className={account.is_enabled ? 'danger' : ''}
                                disabled={toggle.isPending}
                                onClick={() => toggle.mutate({ account, enable: !account.is_enabled })}
                              >
                                {account.is_enabled ? 'Disable' : 'Enable'}
                              </button>
                            </RequirePermission>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>

          {currentExchange ? (
            <ConnectionsPanel
              exchange={currentExchange}
              accounts={currentAccounts}
              onChanged={() => void queryClient.invalidateQueries({ queryKey: ['exchange-accounts'] })}
            />
          ) : null}

          <section className="panel">
            <div className="panel-head">
              <h2>Connector status</h2>
            </div>
            <div className="panel-body">
              <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>
                Connector implemented. Verify it against live public endpoints before enabling a
                live account.
              </p>
              {current === 'kucoin' && (
                <p style={{ color: 'var(--warn)', fontSize: 'var(--text-sm)' }}>
                  KuCoin has no testnet. It retired its sandbox in 2023, so this exchange is
                  validated in simulation and then with a deliberately small live float.
                </p>
              )}
            </div>
          </section>
        </>
      )}
    </>
  );
}

/**
 * What the Start/Pause button may do for an account, mirroring the server.
 *
 * A live account only resumes from Pause. Once stopped (deactivated,
 * disabled, or its credentials changed) it must pass live activation again,
 * and an emergency stop is never cleared from here. The server enforces all
 * of this; the button only avoids offering what would be refused.
 */
export function runControl(account: ExchangeAccount): {
  label: 'Start' | 'Pause';
  blocked: string | null;
  hint: string;
} {
  if (account.trading_state === 'RUNNING') {
    return {
      label: 'Pause',
      blocked: null,
      hint: 'Stop placing new orders. Positions and open orders are untouched.',
    };
  }
  const start = (blocked: string | null) => ({
    label: 'Start' as const,
    blocked,
    hint: 'Allow this account to place orders',
  });
  if (!account.is_enabled) return start('Enable this account first');
  if (account.trading_state === 'EMERGENCY_STOPPED') {
    return start('Emergency stopped. This cannot be started from here.');
  }
  if (account.mode === 'LIVE' && (account.trading_state !== 'PAUSED' || !account.live_activated_at)) {
    return start('A stopped live account must go through live activation again');
  }
  return start(null);
}
